import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Plus } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useToast } from './TechComponents';

/**
 * CartBellWidget — icone panier global en header sidebar (a cote de NotificationBell).
 *
 * Affiche un badge avec le total d'articles cumule sur tous les paniers draft.
 * Au clic : dropdown listant chaque panier + actions rapides
 * (switch + ouvrir le panier, creer un nouveau panier).
 *
 * Visible sur toutes les pages tant que l'utilisateur a la permission ticketing.manage
 * (le hook useCart fetch la liste au mount du provider).
 */
export default function CartBellWidget() {
  const { cart, carts, createNewCart, selectCart, loadCartsList } = useCart();
  const nav = useNavigate();
  const toast = useToast();
  const [open, setOpen] = useState(false);

  // Total items cumule sur tous les paniers draft (badge cloche)
  const totalItems = (carts || []).reduce((s, c) => s + (c.items_count || 0), 0);

  // Refresh la liste a l'ouverture (un autre onglet a pu modifier les paniers)
  useEffect(() => {
    if (open) loadCartsList();
  }, [open, loadCartsList]);

  const onPickCart = async (cartId) => {
    setOpen(false);
    try {
      const target = (carts || []).find((c) => c.id === cartId);
      if (target && !target.is_current) {
        await selectCart(cartId);
      }
      nav('/gestion/panier');
    } catch (e) {
      toast.error(e?.message || 'Erreur ouverture panier');
    }
  };

  const onCreateNew = async () => {
    setOpen(false);
    try {
      await createNewCart('');
      nav('/gestion/panier');
    } catch (e) {
      toast.error(e?.message || 'Erreur creation panier');
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Style aligne 1:1 avec NotificationBell pour aligement vertical parfait */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={`${(carts || []).length} panier(s), ${totalItems} article(s)`}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 6,
          color: 'rgba(255,255,255,0.7)', position: 'relative',
          transition: 'color var(--duration-fast) var(--ease-out)',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; }}
      >
        <ShoppingCart size={20} />
        {totalItems > 0 && (
          <span style={{
            position: 'absolute', top: 0, right: -2,
            background: '#1e40af', color: '#fff',
            fontSize: 9, fontWeight: 800,
            padding: '1px 5px', borderRadius: 10, minWidth: 16, textAlign: 'center',
            fontFamily: "'IBM Plex Mono', monospace",
          }}>
            {totalItems}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Overlay click-to-close (meme pattern que NotificationBell) */}
          <div className="overlay-animate"
               style={{ position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(0,0,0,0.1)' }}
               onClick={() => setOpen(false)} />
          {/* Panel : meme positionnement que NotificationBell (top:10, left:248) */}
          <div className="modal-animate" style={{
            position: 'fixed', top: 10, left: 248,
            width: 380, maxHeight: '90vh', overflowY: 'auto',
            background: 'var(--surface, white)',
            borderRadius: 12,
            border: '1px solid var(--border-light, #e2e8f0)',
            boxShadow: 'var(--shadow-xl, 0 8px 24px rgba(0,0,0,0.18))',
            zIndex: 100,
            color: '#1e293b',
          }}>
          <div
            style={{
              padding: '12px 16px', borderBottom: '1px solid #e2e8f0',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}
          >
            <b style={{ color: '#1e40af', fontSize: '0.92em' }}>Mes paniers</b>
            <span style={{ color: '#94a3b8', fontSize: '0.78em' }}>
              {(carts || []).length} panier(s)
            </span>
          </div>
          <div style={{ maxHeight: 320, overflowY: 'auto' }}>
            {!carts || carts.length === 0 ? (
              <div style={{ padding: 18, textAlign: 'center', color: '#94a3b8', fontSize: '0.85em' }}>
                Aucun panier en cours
              </div>
            ) : (
              carts.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => onPickCart(c.id)}
                  style={{
                    width: '100%', padding: '10px 16px', textAlign: 'left',
                    background: c.id === cart?.id ? '#eef2ff' : 'white', border: 'none',
                    borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>
                    <span style={{ fontWeight: 600, color: '#1e293b' }}>{c.name || 'Brouillon'}</span>
                    {c.id === cart?.id && (
                      <span style={{ marginLeft: 6, fontSize: '0.7em', color: '#1e40af', fontWeight: 700 }}>
                        ● ACTIF
                      </span>
                    )}
                  </span>
                  <span
                    style={{
                      background: '#e2e8f0', padding: '2px 8px', borderRadius: 10,
                      fontSize: '0.78em', color: '#475569', flexShrink: 0,
                    }}
                  >
                    {c.items_count || 0} art.
                  </span>
                </button>
              ))
            )}
          </div>
          <div
            style={{
              padding: '8px 16px', borderTop: '1px solid #e2e8f0',
              display: 'flex', gap: 8,
            }}
          >
            <button
              type="button"
              onClick={onCreateNew}
              style={{
                flex: 1, padding: '6px 10px', background: 'white', border: '1px dashed #94a3b8',
                borderRadius: 6, cursor: 'pointer', fontSize: '0.82em', color: '#475569',
                display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: 'center',
              }}
            >
              <Plus size={12} /> Nouveau panier
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); nav('/gestion/panier'); }}
              style={{
                flex: 1, padding: '6px 10px', background: '#1e40af', color: 'white', border: 'none',
                borderRadius: 6, cursor: 'pointer', fontSize: '0.82em', fontWeight: 600,
              }}
            >
              Ouvrir le panier
            </button>
          </div>
          </div>
        </>
      )}
    </div>
  );
}
